import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/Input';
import { Building2, Users, FolderOpen, ArrowLeft, MoreHorizontal, ExternalLink, Link as LinkIcon, Copy, Check, Edit2, X, Save, Plus, Trash2, FileText, Upload, StickyNote, Paperclip } from 'lucide-react';
type BuilderLink = {
  id: string;
  label: string;
  url: string;
};
type BuilderFile = {
  id: string;
  name: string;
  size: string;
  date: string;
};
const seedLinks: Record<string, BuilderLink[]> = {
  '1': [{
    id: 'l1',
    label: 'Google Drive - Client Files',
    url: 'https://drive.google.com/drive/folders/example'
  }, {
    id: 'l2',
    label: 'Onboarding Document',
    url: 'https://docs.google.com/document/d/example'
  }]
};
const seedNotes: Record<string, string> = {
  '1': 'Primary contact: Sarah M. (sarah@excelhomes.com)\nPrefers email communication.\nOnboarding completed Jan 2024 — all drawings verified.'
};
const seedFiles: Record<string, BuilderFile[]> = {
  '1': [{
    id: 'f1',
    name: 'Excel_Homes_Floorplans.pdf',
    size: '2.4 MB',
    date: 'Jan 15, 2024'
  }, {
    id: 'f2',
    name: 'Option_Pricing_Sheet.xlsx',
    size: '856 KB',
    date: 'Jan 18, 2024'
  }]
};
const builderData: Record<string, any> = {
  '1': {
    name: 'Excel Homes',
    slug: 'excel',
    status: 'Active',
    users: 8,
    buyers: 342,
    joinedDate: '2023-06-15',
    projects: [{
      id: 1,
      name: 'The Riverside Collection',
      status: 'Active',
      buyers: 124,
      lastActivity: '2 hours ago'
    }, {
      id: 2,
      name: 'Oakwood Estates',
      status: 'Active',
      buyers: 89,
      lastActivity: '1 day ago'
    }, {
      id: 3,
      name: 'Maple Ridge',
      status: 'Active',
      buyers: 67,
      lastActivity: '3 hours ago'
    }, {
      id: 4,
      name: 'Cedar Point',
      status: 'Draft',
      buyers: 0,
      lastActivity: '5 days ago'
    }]
  },
  '2': {
    name: 'Schell Brothers',
    slug: 'schell',
    status: 'Active',
    users: 12,
    buyers: 567,
    joinedDate: '2023-03-22',
    projects: [{
      id: 5,
      name: 'Coastal Living',
      status: 'Active',
      buyers: 234,
      lastActivity: '1 hour ago'
    }, {
      id: 6,
      name: 'Beach House Collection',
      status: 'Active',
      buyers: 189,
      lastActivity: '4 hours ago'
    }, {
      id: 7,
      name: 'Seaside Villas',
      status: 'Active',
      buyers: 144,
      lastActivity: '2 days ago'
    }]
  },
  '3': {
    name: 'Mattamy Homes',
    slug: 'mattamy',
    status: 'Active',
    users: 20,
    buyers: 892,
    joinedDate: '2022-11-10',
    projects: [{
      id: 8,
      name: 'Summit Ridge',
      status: 'Active',
      buyers: 312,
      lastActivity: '30 mins ago'
    }, {
      id: 9,
      name: 'Valley View',
      status: 'Active',
      buyers: 278,
      lastActivity: '2 hours ago'
    }, {
      id: 10,
      name: 'Highland Park',
      status: 'Active',
      buyers: 302,
      lastActivity: '5 hours ago'
    }]
  },
  '4': {
    name: 'Landmark Homes',
    slug: 'landmark',
    status: 'Trial',
    users: 3,
    buyers: 45,
    joinedDate: '2024-01-10',
    projects: [{
      id: 11,
      name: 'Pioneer Square',
      status: 'Active',
      buyers: 45,
      lastActivity: '1 day ago'
    }]
  },
  '5': {
    name: 'Starward Homes',
    slug: 'starward',
    status: 'Active',
    users: 6,
    buyers: 234,
    joinedDate: '2023-09-08',
    projects: [{
      id: 12,
      name: 'Horizon Heights',
      status: 'Active',
      buyers: 156,
      lastActivity: '3 hours ago'
    }, {
      id: 13,
      name: 'Skyline Residences',
      status: 'Active',
      buyers: 78,
      lastActivity: '1 day ago'
    }]
  },
  '6': {
    name: 'Brookfield',
    slug: 'brookfield',
    status: 'Active',
    users: 25,
    buyers: 1245,
    joinedDate: '2022-08-20',
    projects: [{
      id: 15,
      name: 'The Meadows',
      status: 'Active',
      buyers: 456,
      lastActivity: '1 hour ago'
    }, {
      id: 16,
      name: 'Riverstone',
      status: 'Active',
      buyers: 389,
      lastActivity: '2 hours ago'
    }, {
      id: 17,
      name: 'Parkview Estates',
      status: 'Active',
      buyers: 400,
      lastActivity: '30 mins ago'
    }]
  }
};
export function BuilderDetailPage() {
  const {
    builderId
  } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const [editedSlug, setEditedSlug] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  // Resources & Notes state
  const [links, setLinks] = useState<BuilderLink[]>([]);
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<BuilderFile[]>([]);
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const builder = builderData[builderId || '1'];
  useEffect(() => {
    if (builder) {
      setEditedSlug(builder.slug);
    }
  }, [builder]);
  // Seed resources data based on builder
  useEffect(() => {
    const id = builderId || '1';
    setLinks(seedLinks[id] || []);
    setNotes(seedNotes[id] || '');
    setFiles(seedFiles[id] || []);
  }, [builderId]);
  if (!builder) {
    return <div className="p-8 text-center" data-id="element-2505">
        <p className="text-slate-500" data-id="element-2506">Builder not found</p>
        <Button onClick={() => navigate('/admin/builders')} className="mt-4" data-id="element-2507">
          Back to Builders
        </Button>
      </div>;
  }
  const filteredProjects = builder.projects.filter((project: any) => project.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleSaveSlug = () => {
    console.log('Saving slug:', editedSlug);
    setIsEditingSlug(false);
  };
  const handleCopyLink = () => {
    const url = `${window.location.origin}/${builder.slug}/dashboard`;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };
  const handleOpenPortal = () => {
    window.open(`/${builder.slug}/dashboard`, '_blank');
  };
  // Resources handlers
  const handleAddLink = () => {
    if (!newLinkLabel.trim() || !newLinkUrl.trim()) return;
    const newLink: BuilderLink = {
      id: `l${Date.now()}`,
      label: newLinkLabel.trim(),
      url: newLinkUrl.trim()
    };
    setLinks(prev => [...prev, newLink]);
    setNewLinkLabel('');
    setNewLinkUrl('');
    setShowAddLink(false);
  };
  const handleDeleteLink = (id: string) => {
    setLinks(prev => prev.filter(l => l.id !== id));
  };
  const handleSaveNotes = () => {
    console.log('Saving notes:', notes);
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2000);
  };
  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    const newFiles: BuilderFile[] = Array.from(selectedFiles).map(f => ({
      id: `f${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: f.name,
      size: f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(0)} KB` : `${(f.size / (1024 * 1024)).toFixed(1)} MB`,
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };
  const handleDeleteFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-2508">
      {/* Header */}
      <div className="flex items-center gap-4" data-id="element-2509">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/builders')} leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-2511" />} data-id="element-2510">
          Back
        </Button>
        <div className="flex-1" data-id="element-2512">
          <div className="flex items-center gap-3" data-id="element-2513">
            <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center" data-id="element-2514">
              <Building2 className="h-6 w-6 text-slate-600" data-id="element-2515" />
            </div>
            <div data-id="element-2516">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-2517">
                {builder.name}
              </h1>
              <p className="text-slate-500" data-id="element-2518">
                Builder company details and projects
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-2519">
          <Badge variant={builder.status === 'Active' ? 'success' : builder.status === 'Trial' ? 'warning' : 'neutral'} data-id="element-2520">
            {builder.status}
          </Badge>
          <Button variant="outline" onClick={handleOpenPortal} leftIcon={<ExternalLink className="h-4 w-4" data-id="element-2522" />} data-id="element-2521">
            Open Portal
          </Button>
        </div>
      </div>

      {/* Portal URL Card */}
      <Card data-id="element-2523">
        <CardHeader data-id="element-2524">
          <div className="flex items-center gap-2" data-id="element-2525">
            <LinkIcon className="h-5 w-5 text-slate-600" data-id="element-2526" />
            <CardTitle data-id="element-2527">Portal URL</CardTitle>
          </div>
        </CardHeader>
        <CardContent data-id="element-2528">
          <div className="flex items-center gap-4" data-id="element-2529">
            {isEditingSlug ? <div className="flex items-center gap-2 flex-1" data-id="element-2530">
                <span className="text-slate-500 font-mono" data-id="element-2531">
                  {window.location.origin}/
                </span>
                <input type="text" value={editedSlug} onChange={e => setEditedSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} className="px-3 py-2 border border-slate-300 rounded-md font-mono focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-2532" />
                <span className="text-slate-500 font-mono" data-id="element-2533">/dashboard</span>
              </div> : <div className="flex items-center gap-2 flex-1" data-id="element-2534">
                <code className="px-4 py-2 bg-slate-100 rounded-lg text-lg font-mono text-slate-800" data-id="element-2535">
                  {window.location.origin}/{builder.slug}/dashboard
                </code>
              </div>}

            <div className="flex items-center gap-2" data-id="element-2536">
              {isEditingSlug ? <>
                  <Button variant="ghost" size="sm" onClick={() => {
                setIsEditingSlug(false);
                setEditedSlug(builder.slug);
              }} data-id="element-2537">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveSlug} leftIcon={<Save className="h-4 w-4" data-id="element-2539" />} data-id="element-2538">
                    Save
                  </Button>
                </> : <>
                  <Button variant="outline" size="sm" onClick={() => setIsEditingSlug(true)} leftIcon={<Edit2 className="h-4 w-4" data-id="element-2541" />} data-id="element-2540">
                    Edit Slug
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleCopyLink} leftIcon={copiedLink ? <Check className="h-4 w-4 text-green-500" data-id="element-2543" /> : <Copy className="h-4 w-4" data-id="element-2544" />} data-id="element-2542">
                    {copiedLink ? 'Copied!' : 'Copy Link'}
                  </Button>
                </>}
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-3" data-id="element-2545">
            Share this URL with {builder.name} to give them access to their
            builder portal.
          </p>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2546">
        <Card data-id="element-2547">
          <CardContent className="p-6" data-id="element-2548">
            <div className="flex items-center gap-3" data-id="element-2549">
              <FolderOpen className="h-5 w-5 text-slate-400" data-id="element-2550" />
              <div data-id="element-2551">
                <p className="text-sm text-slate-500" data-id="element-2552">Projects</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-2553">
                  {builder.projects.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-2554">
          <CardContent className="p-6" data-id="element-2555">
            <div className="flex items-center gap-3" data-id="element-2556">
              <Users className="h-5 w-5 text-slate-400" data-id="element-2557" />
              <div data-id="element-2558">
                <p className="text-sm text-slate-500" data-id="element-2559">Users</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-2560">
                  {builder.users}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-2561">
          <CardContent className="p-6" data-id="element-2562">
            <div className="flex items-center gap-3" data-id="element-2563">
              <Users className="h-5 w-5 text-slate-400" data-id="element-2564" />
              <div data-id="element-2565">
                <p className="text-sm text-slate-500" data-id="element-2566">Total Buyers</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-2567">
                  {builder.buyers}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-2568">
          <CardContent className="p-6" data-id="element-2569">
            <div data-id="element-2570">
              <p className="text-sm text-slate-500" data-id="element-2571">Joined</p>
              <p className="text-lg font-semibold text-slate-900" data-id="element-2572">
                {new Date(builder.joinedDate).toLocaleDateString()}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resources & Notes */}
      <Card data-id="element-2573">
        <CardHeader data-id="element-2574">
          <div className="flex items-center justify-between" data-id="element-2575">
            <div className="flex items-center gap-2" data-id="element-2576">
              <Paperclip className="h-5 w-5 text-slate-600" data-id="element-2577" />
              <CardTitle data-id="element-2578">Resources & Notes</CardTitle>
              <Badge variant="neutral" className="ml-1" data-id="element-2579">
                Internal
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent data-id="element-2580">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2581">
            {/* External Links */}
            <div data-id="element-2582">
              <div className="flex items-center justify-between mb-3" data-id="element-2583">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5" data-id="element-2584">
                  <LinkIcon className="h-4 w-4 text-slate-400" data-id="element-2585" />
                  External Links
                </h4>
                {!showAddLink && <button onClick={() => setShowAddLink(true)} className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors" data-id="element-2586">
                    <Plus className="h-3.5 w-3.5" data-id="element-2587" />
                    Add Link
                  </button>}
              </div>

              {/* Add Link Form */}
              {showAddLink && <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 space-y-2" data-id="element-2588">
                  <input type="text" value={newLinkLabel} onChange={e => setNewLinkLabel(e.target.value)} placeholder="Label (e.g. Google Drive - Client Files)" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-2589" />
                  <input type="url" value={newLinkUrl} onChange={e => setNewLinkUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-2590" />
                  <div className="flex justify-end gap-2" data-id="element-2591">
                    <Button variant="ghost" size="sm" onClick={() => {
                  setShowAddLink(false);
                  setNewLinkLabel('');
                  setNewLinkUrl('');
                }} data-id="element-2592">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleAddLink} disabled={!newLinkLabel.trim() || !newLinkUrl.trim()} leftIcon={<Plus className="h-3.5 w-3.5" data-id="element-2594" />} data-id="element-2593">
                      Save Link
                    </Button>
                  </div>
                </div>}

              {/* Links List */}
              <div className="space-y-2" data-id="element-2595">
                {links.length === 0 && !showAddLink && <div className="text-center py-6 text-slate-400" data-id="element-2596">
                    <LinkIcon className="h-8 w-8 mx-auto mb-2 opacity-40" data-id="element-2597" />
                    <p className="text-xs" data-id="element-2598">No links added yet</p>
                  </div>}
                {links.map(link => <div key={link.id} className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100 group" data-id="element-2599">
                    <ExternalLink className="h-4 w-4 text-slate-400 flex-shrink-0" data-id="element-2600" />
                    <div className="flex-1 min-w-0" data-id="element-2601">
                      <p className="text-sm font-medium text-slate-800 truncate" data-id="element-2602">
                        {link.label}
                      </p>
                      <p className="text-xs text-slate-400 truncate" data-id="element-2603">
                        {link.url}
                      </p>
                    </div>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="p-1 text-slate-400 hover:text-primary-600 transition-colors flex-shrink-0" title="Open link" data-id="element-2604">
                      <ExternalLink className="h-3.5 w-3.5" data-id="element-2605" />
                    </a>
                    <button onClick={() => handleDeleteLink(link.id)} className="p-1 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0" title="Remove link" data-id="element-2606">
                      <Trash2 className="h-3.5 w-3.5" data-id="element-2607" />
                    </button>
                  </div>)}
              </div>
            </div>

            {/* Internal Notes */}
            <div data-id="element-2608">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-3" data-id="element-2609">
                <StickyNote className="h-4 w-4 text-slate-400" data-id="element-2610" />
                Internal Notes
              </h4>
              <textarea value={notes} onChange={e => {
              setNotes(e.target.value);
              setNotesSaved(false);
            }} rows={6} placeholder="Add internal notes about this builder..." className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-primary-500 focus:border-primary-500 resize-none bg-slate-50 placeholder:text-slate-400" data-id="element-2611" />
              <div className="flex items-center justify-end gap-2 mt-2" data-id="element-2612">
                {notesSaved && <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium" data-id="element-2613">
                    <Check className="h-3.5 w-3.5" data-id="element-2614" />
                    Saved!
                  </span>}
                <Button variant="outline" size="sm" onClick={handleSaveNotes} leftIcon={<Save className="h-3.5 w-3.5" data-id="element-2616" />} data-id="element-2615">
                  Save Notes
                </Button>
              </div>
            </div>
          </div>

          {/* File Uploads */}
          <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-2617">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-3" data-id="element-2618">
              <FileText className="h-4 w-4 text-slate-400" data-id="element-2619" />
              Files
            </h4>

            {/* Drop Zone */}
            <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary-400 bg-primary-50' : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'}`} data-id="element-2620">
              <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragging ? 'text-primary-500' : 'text-slate-300'}`} data-id="element-2621" />
              <p className="text-sm text-slate-500" data-id="element-2622">
                {isDragging ? 'Drop files here...' : 'Drop files here or click to upload'}
              </p>
              <p className="text-xs text-slate-400 mt-1" data-id="element-2623">
                PDF, XLSX, DOCX, images, etc.
              </p>
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => handleFileSelect(e.target.files)} data-id="element-2624" />
            </div>

            {/* Files List */}
            {files.length > 0 && <div className="mt-3 space-y-2" data-id="element-2625">
                {files.map(file => {
              const ext = file.name.split('.').pop()?.toLowerCase() || '';
              return <div key={file.id} className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100 group" data-id="element-2626">
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${ext === 'pdf' ? 'bg-red-50 text-red-500' : ext === 'xlsx' || ext === 'xls' ? 'bg-emerald-50 text-emerald-500' : ext === 'docx' || ext === 'doc' ? 'bg-blue-50 text-blue-500' : 'bg-slate-100 text-slate-400'}`} data-id="element-2627">
                        <FileText className="h-4.5 w-4.5" data-id="element-2628" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2629">
                        <p className="text-sm font-medium text-slate-800 truncate" data-id="element-2630">
                          {file.name}
                        </p>
                        <p className="text-xs text-slate-400" data-id="element-2631">
                          {file.size} · {file.date}
                        </p>
                      </div>
                      <button onClick={() => handleDeleteFile(file.id)} className="p-1 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0" title="Remove file" data-id="element-2632">
                        <Trash2 className="h-3.5 w-3.5" data-id="element-2633" />
                      </button>
                    </div>;
            })}
              </div>}
          </div>
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card noPadding data-id="element-2634">
        <CardHeader className="border-b border-slate-200 bg-slate-50" data-id="element-2635">
          <div className="flex items-center justify-between" data-id="element-2636">
            <CardTitle data-id="element-2637">Projects</CardTitle>
            <Button size="sm" data-id="element-2638">Add Project</Button>
          </div>
        </CardHeader>

        <div className="p-4 border-b border-slate-200 bg-slate-50" data-id="element-2639">
          <SearchInput className="max-w-sm bg-white" placeholder="Search projects..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2640" />
        </div>

        <div className="overflow-x-auto" data-id="element-2641">
          <table className="w-full text-left text-sm" data-id="element-2642">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-2643">
              <tr data-id="element-2644">
                <th className="px-6 py-4" data-id="element-2645">Project Name</th>
                <th className="px-6 py-4" data-id="element-2646">Status</th>
                <th className="px-6 py-4" data-id="element-2647">Buyers</th>
                <th className="px-6 py-4" data-id="element-2648">Last Activity</th>
                <th className="px-6 py-4 text-right" data-id="element-2649">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2650">
              {filteredProjects.length === 0 ? <tr data-id="element-2651">
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500" data-id="element-2652">
                    No projects found matching "{searchQuery}"
                  </td>
                </tr> : filteredProjects.map((project: any) => <tr key={project.id} className="hover:bg-slate-50 transition-colors" data-id="element-2653">
                    <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2654">
                      {project.name}
                    </td>
                    <td className="px-6 py-4" data-id="element-2655">
                      <Badge variant={project.status === 'Active' ? 'success' : 'neutral'} data-id="element-2656">
                        {project.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-2657">
                      <div className="flex items-center gap-1" data-id="element-2658">
                        <Users className="h-4 w-4 text-slate-400" data-id="element-2659" />
                        {project.buyers}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-2660">
                      {project.lastActivity}
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2661">
                      <div className="flex items-center justify-end gap-2" data-id="element-2662">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/projects/${project.id}`)} data-id="element-2663">
                          View Details
                        </Button>
                        <Button variant="ghost" size="sm" data-id="element-2664">
                          <MoreHorizontal className="h-4 w-4" data-id="element-2665" />
                        </Button>
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}